const GENERIC_SCHOOL_TOKENS = new Set([
  'high',
  'school',
  'academy',
  'the',
  'of',
  'and',
  'senior',
  'junior',
  'hs',
]);

export const prettySchoolName = (value?: string) => {
  if (!value) return '';
  return value
    .trim()
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
};

const nameTokens = (value: string) =>
  prettySchoolName(value)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

export const pickDisplaySchoolName = (stored?: string, ucName?: string) => {
  const storedPretty = prettySchoolName(stored || '');
  const ucPretty = prettySchoolName(ucName || '');
  if (!ucPretty) return stored || storedPretty;
  if (!storedPretty) return ucPretty;
  if (storedPretty.toLowerCase() === ucPretty.toLowerCase()) return stored || storedPretty;

  const storedTokens = nameTokens(storedPretty);
  const ucTokens = nameTokens(ucPretty);
  const distinctive = storedTokens.filter((token) => !GENERIC_SCHOOL_TOKENS.has(token));
  if (
    distinctive.length > 0 &&
    distinctive.every((token) => ucTokens.includes(token)) &&
    ucTokens.length >= storedTokens.length &&
    ucPretty.length >= storedPretty.length
  ) {
    return ucPretty;
  }
  return stored || storedPretty || '';
};

export const displayHighSchoolName = (school: any): string => {
  if (!school) return '';
  const nested = school.other && typeof school.other === 'object' ? school.other : {};
  const objectSource =
    school.high_school_object && typeof school.high_school_object === 'object'
      ? school.high_school_object
      : nested.high_school_object && typeof nested.high_school_object === 'object'
        ? nested.high_school_object
        : school;
  const stored =
    objectSource.school_name ||
    school.school_name ||
    school.high_school_name ||
    nested.high_school_name ||
    school.id ||
    school.schoolName ||
    '';
  const ucName = objectSource.uc_name || school.uc_name || nested.uc_name || '';
  return pickDisplaySchoolName(stored, ucName);
};

export const highSchoolAliasNames = (item: any): string[] => {
  const primary = prettySchoolName(item?.high_school_point ?? item?.school_name ?? '').toLowerCase();
  return [item?.uc_name, item?.csu_name, item?.cm_name]
    .map((name) => prettySchoolName(name))
    .filter((name) => name && name.toLowerCase() !== primary)
    .filter((name, index, all) => all.findIndex((other) => other.toLowerCase() === name.toLowerCase()) === index);
};

export const buildHighSchoolSearchLabel = (
  item: any,
  getStateName: (stateCode: string) => string
) => {
  const display =
    displayHighSchoolName(item) || prettySchoolName(item?.high_school_point ?? item?.school_name ?? '');
  const stored = prettySchoolName(item?.high_school_point ?? item?.school_name ?? '');
  const aliasPart =
    stored && stored.toLowerCase() !== display.toLowerCase() ? ` / ${stored}` : '';
  return `${display}${aliasPart} (code: ${item?.college_board_code ?? ''}, city: ${item?.city ?? ''}, state: ${getStateName(item?.state ?? '')}, country: ${item?.country ?? ''})`;
};

export const highSchoolMatchesSearch = (item: any, keyword: string) => {
  const tokens = `${keyword ?? ''}`
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!tokens.length) return true;
  const haystack = [
    item?.school_name,
    item?.high_school_point,
    item?.uc_name,
    item?.csu_name,
    item?.cm_name,
    item?.city,
    item?.college_board_code,
    item?.label,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return tokens.every((token) => haystack.includes(token));
};

export const firstSearchToken = (keyword: string) =>
  `${keyword ?? ''}`.trim().split(/\s+/).filter((token) => token.length >= 3)[0] ?? '';
